import { reportOnce } from '@protector/api-client';
import type { UUID } from '@protector/utils/types';
import { selectSelectedProperty } from 'core/core.reducer';
import { ofType, type ActionsObservable, type StateObservable } from 'redux-observable';
import type { AppState } from 'redux/app-state';
import { from, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, toArray, withLatestFrom } from 'rxjs/operators';

import {
  GetEntryWarehouseFailure,
  GetEntryWarehouseSuccess,
  LoadAllTransactionsFromWarehousesFailure,
  LoadAllTransactionsFromWarehousesSuccess,
  LoadCompanyStockWarehouseFailure,
  LoadCompanyStockWarehouseSuccess,
  LoadIndividualStockWarehouseFailure,
  LoadIndividualStockWarehouseSuccess,
  LoadStockWarehouseFailure,
  LoadStockWarehouseSuccess,
  LoadTransactionsWarehouseFailure,
  LoadTransactionsWarehouseSuccess,
  LoadWarehouseFailure,
  LoadWarehouseSuccess,
  NewEntryWarehouseFailure,
  NewEntryWarehouseSuccess,
  NewTransferWarehouseFailure,
  NewTransferWarehouseSuccess,
  NewVendorFailure,
  NewVendorSuccess,
  NewWarehouseFailure,
  NewWarehouseSuccess,
  RemoveWarehouseFailure,
  RemoveWarehouseSuccess,
  UpdateEntryWarehouseFailure,
  UpdateEntryWarehouseSuccess,
  UpdateVendorFailure,
  UpdateVendorSuccess,
  UpdateWarehouseFailure,
  UpdateWarehouseSuccess,
  WarehouseActionsTypes,
  type GetEntryWarehouse,
  type LoadAllTransactionsFromWarehouses,
  type LoadCompanyStockWarehouse,
  type LoadIndividualStockWarehouse,
  type LoadStockWarehouse,
  type LoadTransactionsWarehouse,
  type LoadWarehouse,
  type NewEntryWarehouse,
  type NewTransferWarehouse,
  type NewVendor,
  type NewWarehouse,
  type RemoveWarehouse,
  type UpdateEntryWarehouse,
  type UpdateVendor,
  type UpdateWarehouse
} from './warehouse.actions';
import type { Warehouse, WarehouseTransactionResponse } from '@protector/entities/warehouse/models';
import {
  createWarehouses,
  deleteWarehouses,
  getCompanyStockByWarehouses,
  getIndividualInputWarehousesUrl,
  getIndividualStockByWarehouses,
  getStockByWarehouses,
  getTransactions,
  getWarehouses,
  postIndividualInputWarehousesUrl,
  postVendor,
  putIndividualInputWarehousesUrl,
  transferWarehouses,
  updateVendor,
  updateWarehouses
} from './warehouse.service';

export const handleLoadWarehouse = (action$: ActionsObservable<ReturnType<typeof LoadWarehouse>>) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.LOAD_WAREHOUSE),
    map(action => action.payload),
    concatMap(payload => {
      return getWarehouses(payload).pipe(
        map(response => {
          return LoadWarehouseSuccess(payload, response);
        }),
        catchError((error: string) => of(LoadWarehouseFailure(error)))
      );
    })
  );

export const handleLoadTransactionsWarehouse = (action$: ActionsObservable<ReturnType<typeof LoadTransactionsWarehouse>>) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.LOAD_TRANSACTIONS_WAREHOUSE),
    map(action => action.payload),
    concatMap(payload => {
      return getTransactions(payload).pipe(
        map(response => {
          return LoadTransactionsWarehouseSuccess(payload, response.content);
        }),
        catchError((error: string) => of(LoadTransactionsWarehouseFailure(error)))
      );
    })
  );

export const handleLoadAllTransactionsFromWarehousesId = (
  action$: ActionsObservable<ReturnType<typeof LoadAllTransactionsFromWarehouses>>
) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.LOAD_ALL_TRANSACTIONS_FROM_WAREHOUSES),
    map(action => action.payload),
    mergeMap(uuids =>
      from(
        Promise.all(
          uuids.map(async uuid => {
            const response = await getTransactions(uuid).toPromise();
            if (!response?.content) {
              reportOnce(
                'warehouse.epics:handleLoadAllTransactionsFromWarehouses:missingContent',
                () => new Error('warehouse.epics: getTransactions response.content missing -- falling back to []'),
                { errorType: 'StateFallback', domain: 'Warehouse' }
              );
            }
            return { warehouseId: uuid, transactions: response?.content ?? [] };
          })
        )
      ).pipe(
        map(results => {
          const responseDict = results.reduce((acc: Record<UUID, WarehouseTransactionResponse[]>, response) => {
            acc[response.warehouseId] = response.transactions;
            return acc;
          }, {});

          return LoadAllTransactionsFromWarehousesSuccess(responseDict);
        }),
        catchError((error: string) => of(LoadAllTransactionsFromWarehousesFailure(error)))
      )
    )
  );

export const handleLoadIndividualStockWarehouse = (action$: ActionsObservable<ReturnType<typeof LoadIndividualStockWarehouse>>) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.LOAD_INDIVIDUAL_STOCK_WAREHOUSE),
    map(action => action.payload),
    mergeMap(payload => {
      return getIndividualStockByWarehouses(payload).pipe(
        map(response => {
          if (Array.isArray(response)) {
            return LoadIndividualStockWarehouseSuccess(payload, response);
          }
          return LoadIndividualStockWarehouseFailure('Unexpected response');
        }),
        catchError((error: string) => of(LoadIndividualStockWarehouseFailure(error)))
      );
    })
  );

export const handleLoadCompanyStockWarehouse = (action$: ActionsObservable<ReturnType<typeof LoadCompanyStockWarehouse>>) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.LOAD_COMPANY_STOCK_WAREHOUSE),
    map(action => action.payload),
    concatMap(payload => {
      return getCompanyStockByWarehouses(payload).pipe(
        map(response => {
          if (Array.isArray(response)) {
            return LoadCompanyStockWarehouseSuccess(payload, response);
          }
          return LoadCompanyStockWarehouseFailure('Unexpected response');
        }),
        catchError((error: string) => of(LoadCompanyStockWarehouseFailure(error)))
      );
    })
  );

export const handleLoadStockWarehouse = (
  action$: ActionsObservable<ReturnType<typeof LoadStockWarehouse>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.LOAD_STOCK_WAREHOUSE),
    map(action => action.payload),
    concatMap(companyId => getWarehouses(companyId)),
    withLatestFrom(state$.pipe(map(state => selectSelectedProperty(state.uiState.global)))),
    concatMap(([warehouses, propertySelected]) => {
      const filteredWarehouses = propertySelected
        ? warehouses.filter(warehouse => {
            return warehouse.properties.some(prop => prop.id === propertySelected);
          })
        : warehouses;

      const concurrent = 3;
      return from(filteredWarehouses).pipe(
        mergeMap(warehouse => {
          return getStockByWarehouses(warehouse.id).pipe(
            map(stockArray => {
              const warehouseStock: Warehouse = {
                id: warehouse.id,
                name: warehouse.name,
                properties: warehouse.properties.map(prop => prop.id),
                products: {}
              };

              if (!stockArray.length) {
                return warehouseStock;
              }

              return stockArray.reduce((acc, cur) => {
                return {
                  ...warehouseStock,
                  products: { ...acc.products, [cur.product.id]: cur.balance }
                };
              }, warehouseStock);
            }),
            catchError(() => of('error'))
          );
        }, concurrent),
        toArray()
      );
    }),
    map(warehouses => {
      if (warehouses.includes('error')) {
        return LoadStockWarehouseFailure();
      }
      return LoadStockWarehouseSuccess(warehouses as Warehouse[]);
    })
  );

export const handleCreateWarehouse = (action$: ActionsObservable<ReturnType<typeof NewWarehouse>>) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.NEW_WAREHOUSE),
    map(action => action.payload),
    concatMap(payload => {
      return createWarehouses(payload).pipe(
        map(() => {
          return NewWarehouseSuccess();
        }),
        catchError((error: string) => of(NewWarehouseFailure(error)))
      );
    })
  );

export const handleUpdateWarehouse = (action$: ActionsObservable<ReturnType<typeof UpdateWarehouse>>) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.UPDATE_WAREHOUSE),
    map(action => action.payload),
    concatMap(payload => {
      return updateWarehouses(payload).pipe(
        map(() => {
          return UpdateWarehouseSuccess();
        }),
        catchError((error: string) => of(UpdateWarehouseFailure(error)))
      );
    })
  );

export const handleRemoveWarehouse = (action$: ActionsObservable<ReturnType<typeof RemoveWarehouse>>) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.REMOVE_WAREHOUSE),
    map(action => action.payload),
    concatMap(payload => {
      return deleteWarehouses(payload.warehouseId).pipe(
        map(() => {
          return RemoveWarehouseSuccess(payload);
        }),
        catchError((error: string) => of(RemoveWarehouseFailure(error)))
      );
    })
  );

export const handleTransferWarehouse = (action$: ActionsObservable<ReturnType<typeof NewTransferWarehouse>>) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.NEW_TRANSFER_WAREHOUSE),
    map(action => action.payload),
    concatMap(payload => {
      return transferWarehouses(payload).pipe(
        map(() => {
          return NewTransferWarehouseSuccess();
        }),
        catchError((error: string) => of(NewTransferWarehouseFailure(error)))
      );
    })
  );

export const handleNewVendor = (action$: ActionsObservable<ReturnType<typeof NewVendor>>) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.NEW_VENDOR),
    map(action => action.payload),
    concatMap(payload => {
      return postVendor(payload).pipe(
        map(result => {
          return NewVendorSuccess(result.id);
        }),
        catchError((error: string) => of(NewVendorFailure(error)))
      );
    })
  );

export const handleUpdateVendor = (action$: ActionsObservable<ReturnType<typeof UpdateVendor>>) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.UPDATE_VENDOR),
    map(action => action.payload),
    concatMap(payload => {
      return updateVendor(payload).pipe(
        map(result => {
          return UpdateVendorSuccess(result.id);
        }),
        catchError((error: string) => of(UpdateVendorFailure(error)))
      );
    })
  );

export const handleNewEntryWarehouse = (action$: ActionsObservable<ReturnType<typeof NewEntryWarehouse>>) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.NEW_ENTRY_WAREHOUSE),
    map(action => action.payload),
    concatMap(payload => {
      return postIndividualInputWarehousesUrl(payload.warehouseId ?? '', payload).pipe(
        map(() => {
          return NewEntryWarehouseSuccess();
        }),
        catchError((error: string) => of(NewEntryWarehouseFailure(error)))
      );
    })
  );

export const handleUpdateEntryWarehouse = (action$: ActionsObservable<ReturnType<typeof UpdateEntryWarehouse>>) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.UPDATE_ENTRY_WAREHOUSE),
    map(action => action.payload),
    concatMap(payload => {
      return putIndividualInputWarehousesUrl(payload.warehouseId ?? '', payload).pipe(
        map(() => {
          return UpdateEntryWarehouseSuccess();
        }),
        catchError((error: string) => of(UpdateEntryWarehouseFailure(error)))
      );
    })
  );

export const handleGetEntryWarehouse = (action$: ActionsObservable<ReturnType<typeof GetEntryWarehouse>>) =>
  action$.pipe(
    ofType(WarehouseActionsTypes.GET_ENTRY_WAREHOUSE),
    map(action => action.payload),
    concatMap(payload => {
      return getIndividualInputWarehousesUrl(payload.warehouseId, payload.inputId).pipe(
        map(result => {
          return GetEntryWarehouseSuccess(result);
        }),
        catchError((error: string) => of(GetEntryWarehouseFailure(error)))
      );
    })
  );
